import moment from "moment-timezone";
import { evaluatePricingRules } from "./PricingRuleEngine";
import type {
  AppliedRule,
  PriceBreakdownLine,
  PricingContext,
  PricingRule,
  TicketTypeV2,
} from "./types";

export type RentalHireInput = {
  /** The v2 rental ticket. Only `id` and `price` (pence per fraction) are read. */
  ticket: { id: string; price: number } & Record<string, any>;
  /** The ticket's enabled pricing rules, ladder included. */
  pricingRules?: PricingRule[];
  /** How many fractions the customer picked. */
  fractions: number;
  /** How many units of the equipment they are hiring. */
  units: number;
  /** When the hire starts — drives PEAK_TIME and other time-of-day rules. */
  startTime: Date | string;
  bookingTime?: Date;
  schoolTimezone?: string;
};

export type RentalHirePrice = {
  /** Price of ONE hired unit for the chosen fraction count, pence. */
  unitPrice: number;
  /** `unitPrice x units` — the whole line, pence. */
  total: number;
  breakdown: PriceBreakdownLine[];
  appliedRules: AppliedRule[];
};

/**
 * The single place a rental hire is priced. The widget, basket validation and
 * ChargeRental all call this, so a price ladder cannot mean one thing on the
 * client and another on the server — that drift is exactly what this exists to
 * prevent.
 *
 * Customer tags are deliberately empty: the widget path passes `customerTags:
 * []` (see use-v2-pricing.ts), so MEMBER_PRICING does not apply to rentals on
 * EITHER side. Resolving real tags is a separate change that has to land on
 * both sides at once.
 */
export function priceRentalHire(input: RentalHireInput): RentalHirePrice {
  const {
    ticket,
    pricingRules = [],
    fractions,
    units,
    startTime,
    // DESIGN DECISION, not an oversight — please do not "fix" this.
    //
    // Runtime-local on purpose, and deliberately NOT normalised the way the
    // wall-clock reads below are. On the widget this is the customer's browser
    // clock; on the server it is the container's. A device clock hours out of
    // sync can therefore show an expired EARLY_BIRD as live in the quote.
    //
    // Left as-is, decided 2026-08-27:
    //
    //  - It is the product-wide convention, not a rental quirk.
    //    `use-v2-pricing.ts` passes `bookingTime: new Date()` in three places
    //    for every OTHER ticket type and has done since long before this
    //    feature. Sending rentals a server clock would make them the only
    //    widget surface that does not price the way the rest of the product
    //    prices.
    //  - Nothing is mispriced. `ValidateBasketItems` and `ChargeRental`
    //    recompute with the server's clock and their figure is what is
    //    charged, so the worst case is a total that visibly changes at
    //    checkout — never a customer buying at a price the rules disallow, and
    //    never an exploit, since setting a clock back unlocks nothing.
    //  - There is no server clock in the widget payload to use.
    //    `GetCourseWithBundles` ships no timestamp, so a fix means adding one
    //    and threading it to every pricing surface — a product-wide change
    //    that belongs on its own branch with its own QA, not on this one.
    //
    // The exposure IS new for rentals, in the same way as the flat-price
    // discount above: a hire evaluated no rules at all before this feature, so
    // there was no early-bird on a rental to be skewed. The pre-deploy query
    // in the plan lists any such rule that exists.
    bookingTime = new Date(),
    schoolTimezone = "Europe/London",
  } = input;

  // Clamp rather than trust. A zero or negative count reaching the engine would
  // price the hire at nothing. The real bounds are the ticket's
  // rentalMinFractions / rentalMaxFractions, enforced by ChargeRental and — as
  // of the review of 2026-08-26 — by ValidateBasketItems' resolveRentalItemPrice
  // as well, because ChargeRental runs after the customer has paid. Clamping
  // here is a floor under the arithmetic, never a bounds check: it makes an
  // out-of-bounds length price plausibly rather than absurdly, which is exactly
  // why the callers have to refuse it before the money moves.
  const safeFractions = Math.max(1, Math.floor(Number(fractions) || 1));
  const safeUnits = Math.max(1, Math.floor(Number(units) || 1));

  // Pre-multiplying by the fraction count is what makes a ladder-less rental
  // price EXACTLY as it did before tiers existed: price x fractions x units.
  // Rules that do not apply leave this base untouched.
  // Floored like the other two. A negative `ticket.price` used to pass through
  // untouched (`Number(-500) || 0` is `-500`), so the hire totalled negative
  // and `resolveRentalItemPrice` returned that as the item price — subtracting
  // from the basket rather than being refused.
  const basePrice = Math.max(0, Number(ticket.price) || 0) * safeFractions;

  const start = startTime instanceof Date ? startTime : new Date(startTime);

  // Every other caller of this engine reads `.getDay()`/`.getHours()` in
  // whatever timezone the RUNTIME happens to be in — safe for them because,
  // before pricing rules existed, the widget applied none and the server
  // trusted the widget's price, so the two could never disagree. Rentals
  // broke that: the server's price now wins. Runtime-local reads a different
  // wall-clock hour on the browser (customer's zone) than on the server
  // (container, UTC) for the exact same instant — a PEAK_TIME rule keyed on
  // "09:00-17:00" could surcharge on one side and not the other for a single
  // 09:00 hire, and the customer would watch the total change at checkout.
  // Deriving from `schoolTimezone` instead makes both sides read the same
  // wall-clock time regardless of where each one runs. Scoped to this
  // function only — every other caller keeps the runtime-local convention.
  const startMoment = moment.tz(start, schoolTimezone);
  const context: PricingContext = {
    ticketType: ticket as unknown as TicketTypeV2,
    quantity: safeUnits,
    totalTicketsInCart: safeUnits,
    sessionsCount: 1,
    sessionStartTime: start,
    bookingTime,
    sessionDayOfWeek: startMoment.day(),
    sessionTimeOfDay: startMoment.format("HH:mm"),
    // Same normalisation, for the date-keyed conditions. Without these,
    // MONTH_RANGE reads the calendar date off the raw instant in whatever zone
    // the runtime is in — the browser's on the widget, UTC in the container —
    // so a hire near midnight fell inside the range on one side and outside it
    // on the other, which is the divergence this whole block exists to stop.
    sessionMonth: startMoment.month() + 1,
    sessionDayOfMonth: startMoment.date(),
    sessionDateISO: startMoment.format("YYYY-MM-DD"),
    customerTags: [],
    schoolTimezone,
    fractions: safeFractions,
  };

  // Filtered HERE, not in `mapDbRentalPricingRules`, because the widget does
  // not go through the mapper — `checkout-rental.tsx` hands this function the
  // ticket's raw rule list. Excluding these in the mapper meant the two server
  // callers dropped them and the widget did not, so a rental carrying one was
  // quoted at the override price and charged at the ladder price: a client and
  // server disagreement, which is the single thing this function exists to
  // make impossible. `priceRentalHire` is the only place all three callers
  // share, so the rule about what a rental's price shape is belongs in it.
  const rentalRules = (pricingRules ?? []).filter((r) => !isNonRentalPriceOverride(r));

  const result = evaluatePricingRules(basePrice, rentalRules, context);

  return {
    unitPrice: result.finalPrice,
    total: result.totalPerOrder,
    breakdown: result.breakdown,
    appliedRules: result.appliedRules,
  };
}

/** Price-override rules that shape a price by QUANTITY or CAPACITY. On a
 *  rental they are meaningless — they replace the base price with a formula
 *  that ignores how long the thing is hired for — and before this feature they
 *  were inert on every rental surface, because none of them evaluated pricing
 *  rules at all. Routing rentals through the engine would have quietly made
 *  them live, mispricing hires by dropping their duration.
 *
 *  A rental's price shape is its ladder, or its flat per-fraction price. So
 *  these are dropped here rather than at each call site, and the ladder plus
 *  every modifier rule (EARLY_BIRD, PEAK_TIME, VOLUME_DISCOUNT, ...) still
 *  apply — those compose with a hire price perfectly well.
 *
 *  This is also what makes it safe for the dashboard to STOP deleting such a
 *  rule when a rental is saved without a ladder: preserved, it is now inert
 *  rather than silently repricing hires from a surface that cannot delete it. */
// DESIGN DECISION, not an oversight — please do not "fix" this.
//
// This set covers rule TYPES whose whole shape ignores duration. It does NOT
// cover a discount rule configured with `fixedPrice`, `pricePerUnit` or
// `pricePerSession`, even though `applyDiscountToPrice` returns those verbatim
// and so flattens a hire the same way: under a £10 fixed-price rule a 2-hour
// and a 10-hour hire both cost £10 per unit.
//
// Left alone deliberately, decided 2026-08-27:
//
//  - It is the school's own configuration, applied faithfully. "Fixed price
//    £10" means £10; honouring that is not a bug.
//  - It behaves identically on every other ticket type and always has. Only
//    `percentage` and `flat` scale with duration, because only they take the
//    duration-scaled base as input; the other three replace it. Rentals now
//    match the rest of the product rather than diverging from it.
//  - Client and server agree — both run this same function — so nobody is ever
//    charged something other than what they were shown.
//  - Nothing here is reachable by a customer. A school has to have chosen one
//    of those three discount types on a rental ticket.
//
// Deploy note: such a rule was inert on rentals before this feature, because
// no rental surface evaluated pricing rules at all. It becomes live on deploy,
// which is what the pre-deploy query in the plan exists to surface.
const NON_RENTAL_SHAPE_RULES = new Set([
  "TIERED_PRICING",
  "GROUP_SIZE",
  "SLIDING_SCALE",
  "CAPACITY_BASED_PRICING",
]);

/** Just the fields these two predicates read. Structural on purpose: the
 *  callers hold a `PricingRule`, a Prisma row with its own nested relation
 *  types, and a widget payload, and none of those are assignable to each
 *  other. */
export type RentalRuleShape = {
  ruleType?: string | null;
  modificationAction?: any;
  discountType?: string | null;
  config?: any;
};

/**
 * Whether a rule would REPLACE a rental's duration-scaled base price with
 * something that ignores how long the hire is.
 *
 * Not a `ruleType` test, because it cannot be: `evaluatePricingRules` routes a
 * CONDITIONAL_DISCOUNT into the price-override set too when its
 * `modificationAction.type` is `absolute_override` (or, on the legacy shape,
 * `discountType === "price_override"`). Such a rule flattens a 10-fraction
 * hire and a 1-fraction hire to the same figure — exactly the failure the
 * quantity-shaped types above are excluded for.
 */
export function isNonRentalPriceOverride(r: RentalRuleShape | null | undefined): boolean {
  if (!r) return false;
  if (r.ruleType === "CONDITIONAL_DISCOUNT") {
    return r.modificationAction
      ? r.modificationAction.type === "absolute_override"
      : r.discountType === "price_override";
  }
  return NON_RENTAL_SHAPE_RULES.has(r.ruleType ?? "");
}

/**
 * Whether a rule can move a rental's price at all — the question every surface
 * that DESCRIBES a rental price needs answered, as opposed to the narrower one
 * `priceRentalHire` needs when deciding what to feed the engine.
 *
 * Two ways to be incapable:
 *
 *  - FILTERED OUT before evaluation, by `isNonRentalPriceOverride` above.
 *  - INERT once evaluated, because `priceRentalHire` builds a context that
 *    cannot satisfy the rule. A rental context carries `fractions`,
 *    `quantity`, a start instant and a timezone and nothing else, so anything
 *    keyed on what it omits declines every time:
 *      `cart` absent      -> BOGO
 *      `customerTags` []  -> MEMBER_PRICING
 *      `sessionsCount` 1  -> MULTI_SESSION
 *
 * The last two are judged on their CONFIG, not their type, because each has a
 * degenerate shape that genuinely applies — an "all"-match member rule with no
 * required tags (`[].every()` is true), a multi-session rule needing fewer
 * than two — and hiding a discount the customer really receives is its own
 * kind of lie. "All"-match is load-bearing there: an "any"-match rule with no
 * tags declines like the rest.
 *
 * Lives here, beside the context that causes all of it, because it had grown
 * two hand-synced copies — one in the checkout summary, one about to appear in
 * the dashboard's price-flow view — and every one of its four extensions so
 * far was found by review rather than by the copies disagreeing loudly.
 */
export function cannotAffectRentalPrice(rule: RentalRuleShape): boolean {
  if (isNonRentalPriceOverride(rule)) return true;
  if (rule.ruleType === "MEMBER_PRICING") {
    return rule.config?.matchType === "any"
      ? true
      : (rule.config?.requiredTags ?? []).length > 0;
  }
  if (rule.ruleType === "MULTI_SESSION") {
    return (rule.config?.minSessions ?? 0) >= 2;
  }
  return rule.ruleType === "BOGO";
}

/**
 * Adapts a Prisma `ticketTypeV2.pricingRules` query result — the raw
 * `PricingRule` rows with their nested `PricingRuleTier` rows straight off the
 * DB — into the `pricingRules` shape `priceRentalHire` expects, narrowing each
 * rule's `tiers` down to the three fields the engine reads (`minFractions`,
 * `priceMode`, `price`) and dropping the tier rows' own Prisma bookkeeping
 * columns (`id`, `pricingRuleId`, `createdAt`, `updatedAt`).
 *
 * The one adapter for both rental price call sites — ChargeRental and
 * ValidateBasketItems' resolveRentalItemPrice both load this relation the
 * same way and fed priceRentalHire from two hand-copied inline maps before
 * this existed, which is exactly the kind of drift priceRentalHire itself
 * exists to prevent.
 */
export function mapDbRentalPricingRules(
  rules: Array<Record<string, any>> | null | undefined,
): PricingRule[] {
  return (rules ?? [])
    .map((r) => ({
      ...r,
      tiers: r.tiers?.map((t: any) => ({
        minFractions: t.minFractions,
        priceMode: t.priceMode,
        price: t.price,
      })),
    })) as PricingRule[];
}
